/* JC-EVO 09/26/2026: product cards design A — shelf paddles + "Choose" to the product page (see the end of the file).
   Two grey chevron buttons under every Horizon product
   carousel (product-list sections, layout "carousel"); styles in assets/jc-product-cards.css (hidden on phones: swipe).
   They scroll the carousel's own scroller by the whole tiles in view; Horizon's scroll-snap and slide tracking stay in
   charge. Hidden when everything fits; disabled at either end. Loaded by sections/jc-header.liquid (defer).
   Sets data-jc-shelf-nav on the carousel once the paddles exist (the CSS then hides Horizon's own arrows).
   Horizon names used: .section-resource-list, slideshow-component.resource-list__carousel, slideshow-slides,
   slideshow-slide, .resource-list, product-card. */
(() => {
  const chevron = (d) => `<svg viewBox="0 0 36 36" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const LEFT = 'M20.5 11.5 14 18l6.5 6.5';
  const RIGHT = 'M15.5 11.5 22 18l-6.5 6.5';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setup = (sc) => {
    if (sc.dataset.jcShelf || !sc.querySelector('product-card')) return;
    const scroller = sc.querySelector('slideshow-slides');
    const list = sc.closest('.resource-list');
    if (!scroller || !list) return;
    sc.dataset.jcShelf = '1';
    const nav = document.createElement('div');
    nav.className = 'jc-shelf-nav';
    const make = (dir, label, path) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `jc-shelf-nav__btn jc-shelf-nav__btn--${dir}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = chevron(path);
      b.addEventListener('click', () => {
        const slide = scroller.querySelector('slideshow-slide:not([hidden])');
        if (!slide) return;
        const step = slide.getBoundingClientRect().width + (parseFloat(getComputedStyle(scroller).columnGap) || 0);
        if (!step) return;
        const n = Math.max(1, Math.floor((scroller.clientWidth + 1) / step));
        scroller.scrollBy({ left: (dir === 'next' ? 1 : -1) * step * n, behavior: reduce.matches ? 'auto' : 'smooth' });
      });
      return b;
    };
    const prev = make('prev', 'Previous slide', LEFT);
    const next = make('next', 'Next slide', RIGHT);
    nav.append(prev, next);
    list.after(nav);
    sc.setAttribute('data-jc-shelf-nav', '');   // CSS hides Horizon's own arrows only once the paddles exist
    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      nav.hidden = max <= 4;
      prev.disabled = scroller.scrollLeft <= 4;
      next.disabled = scroller.scrollLeft >= max - 4;
    };
    scroller.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(scroller);
    update();
    // JC-EVO 09/30/2026 a11y (WCAG 4.1.2 / 2.4.3 / 2.4.7 / 2.1.1, audit T4): Horizon marks slides outside the view aria-hidden="true"
    // but left their links and buttons in the Tab order, so keyboard focus landed on cards a screen reader cannot see, often off
    // screen. A hidden slide is now inert (this observer is its only owner; it mirrors Horizon's aria-hidden), and keyboards reach
    // the rest by scrolling the shelf: its scroller is a focusable, labelled region (arrow keys; the paddles on wider screens). A card
    // peeking in at the edge is inert too, so it is clickable once scrolled in. Focus inside a slide that hides moves to the region.
    if (!scroller.hasAttribute('data-jc-region')) {
      const heading = sc.closest('.section-resource-list')?.querySelector('h1, h2, h3, h4');
      scroller.setAttribute('data-jc-region', '');
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('role', 'region');
      scroller.setAttribute('aria-label', (heading && heading.textContent.trim().replace(/\s+/g, ' ')) || 'Products');
    }
    const syncAll = () => scroller.querySelectorAll(':scope > slideshow-slide').forEach((slide) => {
      const hide = slide.getAttribute('aria-hidden') === 'true';
      if (slide.inert === hide) return;
      if (hide && slide.contains(document.activeElement)) scroller.focus({ preventScroll: true });
      slide.inert = hide;
    });
    new MutationObserver(syncAll).observe(scroller, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'] });
    syncAll();
  };

  const init = (root) => (root || document)
    .querySelectorAll('.section-resource-list slideshow-component.resource-list__carousel')
    .forEach((sc) => {
      try { setup(sc); } catch (e) { /* a paddle failure must never break the page */ }
    });

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* "Choose" (multi-variant quick-add) opens the product page. FAST's product templates have Horizon's product-information
     section disabled, so Horizon's quick-add sheet would open empty (main 09/26, option 2). The listener sits on window in
     the capture phase, ahead of Horizon's delegated document handlers; Enter/Space on the button fire the same click.
     No sheet, no fetch on click. "Add" (single variant) is untouched. The URL is the card's own (Horizon keeps ?variant=). */
  const CHOOSE = 'product-card quick-add-component .quick-add__button--choose';
  const titleOf = (btn) => {
    const link = btn.closest('product-card')?.querySelector('.product-card__link');
    return ((link && link.getAttribute('aria-label')) || '').trim();
  };
  const label = (root) => (root || document).querySelectorAll(CHOOSE).forEach((btn) => {
    const t = titleOf(btn);
    if (t && btn.getAttribute('aria-label') !== `Choose options for ${t}`) btn.setAttribute('aria-label', `Choose options for ${t}`);
  });
  window.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest(CHOOSE) : null;
    if (!btn) return;
    const card = btn.closest('product-card');
    let url = '';
    try { url = card.productPageUrl || ''; } catch (err) { url = ''; }
    if (!url) url = card.querySelector('a.product-card__link[href]')?.href || '';
    if (!url) return;                                   // no URL: leave it to Horizon
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.metaKey || e.ctrlKey) window.open(url, '_blank', 'noopener');
    else window.location.assign(url);
  }, true);
  label();
  // filtered / infinite-scrolled grids re-render cards (Horizon morphs, which can also drop the label): label them again
  const results = document.getElementById('ResultsList');
  const host = results && (results.closest('[id^="shopify-section-"]') || results.parentElement);
  if (host && 'MutationObserver' in window) {
    let queued = 0;
    new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => { queued = 0; label(host); });
    }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
  }
  document.addEventListener('shopify:section:load', (e) => label(e.target));
})();
